import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { Button, Flex, Text, TextField } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import { CreateGdprEventRequest } from "@/v2/entities/gdpr-event/GdprEvent.model";

interface CreateGdprEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (request: CreateGdprEventRequest) => Promise<boolean>;
  isSubmitting: boolean;
}

function toIsoString(value: string): string {
  return new Date(value).toISOString();
}

function toLocalDateTimeInputValue(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function CreateGdprEventDialog({
  open,
  onOpenChange,
  onCreate,
  isSubmitting,
}: CreateGdprEventDialogProps) {
  const now = useMemo(() => new Date(), []);
  const defaultToDate = useMemo(() => toLocalDateTimeInputValue(now), [now]);
  const defaultFromDate = useMemo(() => {
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return toLocalDateTimeInputValue(thirtyDaysAgo);
  }, [now]);

  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);
  const [error, setError] = useState<string>("");

  const resetState = useCallback(() => {
    setFromDate(defaultFromDate);
    setToDate(defaultToDate);
    setError("");
  }, [defaultFromDate, defaultToDate]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        resetState();
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange, resetState]
  );

  const handleFromDateChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFromDate(event.target.value);
    },
    []
  );

  const handleToDateChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setToDate(event.target.value);
    },
    []
  );

  const handleCancel = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const validate = useCallback((): boolean => {
    if (!fromDate || !toDate) {
      setError("From date and to date are required.");
      return false;
    }

    const fromTimestamp = new Date(fromDate).getTime();
    const toTimestamp = new Date(toDate).getTime();

    if (Number.isNaN(fromTimestamp) || Number.isNaN(toTimestamp)) {
      setError("Please provide valid dates.");
      return false;
    }

    if (fromTimestamp > toTimestamp) {
      setError("From date must be earlier than to date.");
      return false;
    }

    setError("");
    return true;
  }, [fromDate, toDate]);

  const handleCreate = useCallback(async () => {
    if (!validate()) {
      return;
    }

    const didCreate = await onCreate({
      fromDate: toIsoString(fromDate),
      toDate: toIsoString(toDate),
    });
    if (didCreate) {
      handleOpenChange(false);
    }
  }, [fromDate, handleOpenChange, onCreate, toDate, validate]);

  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content size="small">
        <Flex direction="column" gap="4" p="4">
          <Dialog.Title>Create GDPR Event</Dialog.Title>
          <Text size="2" color="gray">
            Start a GDPR anonymization run for tickets in a selected date range.
          </Text>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">
                From Date
              </Text>
              <TextField.Root
                type="datetime-local"
                value={fromDate}
                onChange={handleFromDateChange}
              />
            </Flex>

            <Flex direction="column" gap="2">
              <Text size="2" weight="medium">
                To Date
              </Text>
              <TextField.Root
                type="datetime-local"
                value={toDate}
                onChange={handleToDateChange}
              />
            </Flex>

            {error ? (
              <Text size="1" color="red">
                {error}
              </Text>
            ) : null}
          </Flex>

          <Flex gap="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isSubmitting} highContrast>
              Create Event
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}

